import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import socket from '../utils/socket';
import { SkelList } from '../components/Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Instagram-desktop style comment moderation:
//  left  = the connected account's posts/reels (grid, with unread badges)
//  right = the selected post's media + its comment thread, with reply/delete/hide
export default function Comments() {
  const { workspaceUuid } = useParams();

  const [connections, setConnections] = useState([]);
  const [activeConn, setActiveConn] = useState(null);
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [selected, setSelected] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [error, setError] = useState('');
  const [replyTo, setReplyTo] = useState(null);   // comment id being replied to
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const selectedRef = useRef(null);
  const connRef = useRef(null);
  selectedRef.current = selected;
  connRef.current = activeConn;

  const connQS = () => (activeConn ? `&connectionId=${activeConn}` : '');

  // ---- connections (tabs) ----
  useEffect(() => {
    if (!workspaceUuid) return;
    axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/integrations`).then(r => {
      const list = r.data?.instagram || [];
      setConnections(list);
      setActiveConn(prev => (list.find(c => c.connId === prev) ? prev : (list[0]?.connId || null)));
    }).catch(() => { setConnections([]); setActiveConn(null); });
  }, [workspaceUuid]);

  // ---- media list ----
  const loadMedia = useCallback(async () => {
    if (!workspaceUuid) return;
    setLoadingMedia(true); setError('');
    try {
      const r = await axios.get(`${API_URL}/api/integrations/instagram/media?workspaceUuid=${workspaceUuid}${activeConn ? `&connectionId=${activeConn}` : ''}`);
      setMedia(r.data?.media || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load posts. Reconnect Instagram if this persists.');
      setMedia([]);
    } finally {
      setLoadingMedia(false);
    }
  }, [workspaceUuid, activeConn]);

  useEffect(() => { loadMedia(); setSelected(null); setComments([]); }, [loadMedia]);

  // Auto-open the first post once the media list has loaded (nothing selected yet).
  useEffect(() => {
    if (!loadingMedia && media.length > 0 && !selected) openPost(media[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMedia, media]);

  // ---- comments for the selected post ----
  const openPost = useCallback(async (m) => {
    setSelected(m); setComments([]); setReplyTo(null); setReplyText('');
    setLoadingComments(true);
    try {
      const r = await axios.get(`${API_URL}/api/integrations/instagram/media/${m.id}/comments?workspaceUuid=${workspaceUuid}${connQS()}`);
      setComments(r.data?.comments || []);
      // opening clears this post's unread locally
      setMedia(prev => prev.map(x => x.id === m.id ? { ...x, unread: 0 } : x));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load comments.');
    } finally {
      setLoadingComments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, activeConn]);

  // ---- live updates ----
  useEffect(() => {
    if (!workspaceUuid) return;
    socket.emit('joinWorkspace', workspaceUuid);
    const onNew = ({ mediaId }) => {
      // bump the post's unread in the grid
      setMedia(prev => prev.map(x => x.id === mediaId ? { ...x, unread: (x.unread || 0) + 1, comments_count: (x.comments_count || 0) + 1 } : x));
      // if that post is open, refresh its thread
      if (selectedRef.current && selectedRef.current.id === mediaId) {
        axios.get(`${API_URL}/api/integrations/instagram/media/${mediaId}/comments?workspaceUuid=${workspaceUuid}${connRef.current ? `&connectionId=${connRef.current}` : ''}`)
          .then(r => setComments(r.data?.comments || [])).catch(() => {});
      }
    };
    socket.on('igCommentNew', onNew);
    return () => { socket.off('igCommentNew', onNew); };
  }, [workspaceUuid]);

  // ---- actions ----
  const submitReply = async (commentId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/integrations/instagram/comments/${commentId}/reply`, {
        workspaceUuid, message: replyText.trim(), connectionId: activeConn || undefined,
      });
      setReplyText(''); setReplyTo(null);
      await openPost(selected); // refresh thread
    } catch (e) {
      alert('Reply failed: ' + (e.response?.data?.error || e.message) + '\n(If it mentions permissions, reconnect Instagram to grant comment access.)');
    } finally { setSending(false); }
  };

  const del = async (commentId) => {
    if (!window.confirm('Delete this comment permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/integrations/instagram/comments/${commentId}?workspaceUuid=${workspaceUuid}${connQS()}`);
      setComments(prev => prev.filter(c => c.id !== commentId).map(c => ({ ...c, replies: (c.replies || []).filter(r => r.id !== commentId) })));
    } catch (e) { alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };

  const toggleHide = async (c) => {
    try {
      await axios.post(`${API_URL}/api/integrations/instagram/comments/${c.id}/hide`, {
        workspaceUuid, hide: !c.hidden, connectionId: activeConn || undefined,
      });
      setComments(prev => prev.map(x => x.id === c.id ? { ...x, hidden: !x.hidden } : x));
    } catch (e) { alert('Hide failed: ' + (e.response?.data?.error || e.message)); }
  };

  const isVideo = (m) => m.media_type === 'VIDEO' || m.media_product_type === 'REELS';
  const thumb = (m) => m.thumbnail_url || m.media_url;
  const igUser = (connections.find(c => c.connId === activeConn)?.label) || 'instagram';
  const selIdx = selected ? media.findIndex(m => m.id === selected.id) : -1;
  const prevM = selIdx > 0 ? media[selIdx - 1] : null;
  const nextM = selIdx >= 0 && selIdx < media.length - 1 ? media[selIdx + 1] : null;

  return (
    <div className="cmt-wrap">
      {/* ------- left: posts ------- */}
      <div className="cmt-media-col">
        <div className="cmt-col-head">
          <span>Posts & Reels</span>
          {media.length > 0 && <span className="cmt-count">{media.length}</span>}
        </div>

        {connections.length > 1 && (
          <div className="cmt-tabs">
            {connections.map(c => (
              <button key={c.connId} className={`cmt-tab${activeConn === c.connId ? ' on' : ''}`} onClick={() => setActiveConn(c.connId)} title={c.label}>
                {c.label}
              </button>
            ))}
          </div>
        )}

        {loadingMedia ? (
          <SkelList rows={6} />
        ) : media.length === 0 ? (
          <div className="cmt-empty"><p>{error || 'No posts found for this account.'}</p></div>
        ) : (
          <div className="cmt-grid">
            {media.map(m => (
              <button key={m.id} className={`cmt-thumb${selected?.id === m.id ? ' on' : ''}`} onClick={() => openPost(m)}>
                {thumb(m)
                  ? <img src={thumb(m)} alt="" loading="lazy" />
                  : <div className="cmt-thumb-ph">{isVideo(m)
                      ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg>
                      : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>}</div>}
                {isVideo(m) && <span className="cmt-reel"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg></span>}
                <span className="cmt-thumb-meta"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: '-2px' }}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg> {m.comments_count || 0}</span>
                {m.unread > 0 && <span className="cmt-unread">{m.unread}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ------- right: selected post ------- */}
      <div className="cmt-detail">
        {!selected ? (
          <div className="cmt-detail-empty">
            <div className="cmt-empty-ic">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>
            <h2>No post selected</h2>
            <p>Select a post or reel to view and manage its comments.</p>
          </div>
        ) : (
          <>
            <div className="cmt-media-view">
              <button className="cmt-nav" disabled={!prevM} onClick={() => prevM && openPost(prevM)} aria-label="Previous post" title="Previous post">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <div className="cmt-phone">
                <div className="cmt-phone-head">
                  <span className="cmt-phone-av">{(igUser || 'i').replace('@', '').charAt(0).toUpperCase()}</span>
                  <div className="cmt-phone-uwrap">
                    <span className="cmt-phone-user">{igUser}</span>
                    <span className="cmt-phone-sub">{isVideo(selected) ? 'Reel' : 'Post'}</span>
                  </div>
                  <svg className="cmt-phone-more" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" /></svg>
                </div>
                <div className="cmt-phone-media">
                  {isVideo(selected) && selected.media_url
                    ? <video src={selected.media_url} poster={selected.thumbnail_url} controls className="cmt-media-el" />
                    : <img src={selected.media_url || selected.thumbnail_url} alt="" className="cmt-media-el" />}
                </div>
                <div className="cmt-phone-bar">
                  <div className="cmt-phone-acts">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                  </div>
                  <svg className="cmt-phone-save" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                </div>
                <div className="cmt-phone-cap">
                  <b>{(igUser || '').replace('@', '')}</b>
                  <span>{selected.caption || 'No caption'}</span>
                </div>
              </div>
              <button className="cmt-nav" disabled={!nextM} onClick={() => nextM && openPost(nextM)} aria-label="Next post" title="Next post">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>

            <div className="cmt-thread">
              <div className="cmt-thread-head">
                <div className="cmt-caption">{selected.caption || <span className="cmt-dim">No caption</span>}</div>
                <a href={selected.permalink} target="_blank" rel="noreferrer" className="cmt-open-ig">Open in Instagram ↗</a>
              </div>

              <div className="cmt-list">
                {loadingComments ? (
                  <SkelList rows={7} />
                ) : comments.length === 0 ? (
                  <div className="cmt-empty"><p>No comments on this post yet.</p></div>
                ) : comments.map(c => (
                  <div key={c.id} className={`cmt-item${c.hidden ? ' hidden' : ''}`}>
                    <div className="cmt-avatar">{(c.username || '?').charAt(0).toUpperCase()}</div>
                    <div className="cmt-body">
                      <div className="cmt-line">
                        <b>{c.username || 'user'}</b>{c.mine && <span className="cmt-you">you</span>}
                        <span className="cmt-text">{c.text}</span>
                      </div>
                      <div className="cmt-actions">
                        <span className="cmt-time">{c.timestamp ? new Date(c.timestamp).toLocaleString() : ''}</span>
                        {typeof c.like_count === 'number' && c.like_count > 0 && <span><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ verticalAlign: '-2px' }}><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg> {c.like_count}</span>}
                        <button onClick={() => { setReplyTo(c.id); setReplyText(''); }}>Reply</button>
                        <button onClick={() => toggleHide(c)}>{c.hidden ? 'Unhide' : 'Hide'}</button>
                        <button className="cmt-del" onClick={() => del(c.id)}>Delete</button>
                      </div>

                      {(c.replies || []).map(r => (
                        <div key={r.id} className="cmt-reply">
                          <div className="cmt-avatar sm">{(r.username || '?').charAt(0).toUpperCase()}</div>
                          <div className="cmt-body">
                            <div className="cmt-line"><b>{r.username || 'user'}</b>{r.mine && <span className="cmt-you">you</span>}<span className="cmt-text">{r.text}</span></div>
                            <div className="cmt-actions">
                              <span className="cmt-time">{r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}</span>
                              <button className="cmt-del" onClick={() => del(r.id)}>Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {replyTo === c.id && (
                        <div className="cmt-replybox">
                          <input autoFocus value={replyText} onChange={e => setReplyText(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') submitReply(c.id); }}
                            placeholder={`Reply to ${c.username || 'user'}…`} />
                          <button disabled={sending || !replyText.trim()} onClick={() => submitReply(c.id)}>{sending ? '…' : 'Send'}</button>
                          <button className="cmt-cancel" onClick={() => { setReplyTo(null); setReplyText(''); }}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
